// @ts-strict-ignore
import { isNil, isNumber } from 'lodash';
import React from 'react';
import { PixelRatio } from 'react-native';
import { View } from '@shared/components/View';
import { MBCircularProgressBar, UnvalidatedStyles } from '@shared/components/loading';
import { useImageSrcAndDimensions } from '@shared/util/hooks';
import { MBChatAttachmentProps } from '../index';

// the smallest dimensions to render the image bubble
// sized to the loading spinner with some pad.
const MIN_IMAGE_SIZE = UnvalidatedStyles.circularProgressBarSize.normal + 20;

// the tallest to allow an image to render, in vh
const MAX_VIEWPORT_HEIGHT_PCT = 60;

// Calculate the maximum dims we'll request from imgix for attachment bubbles.
// Use 75% of the screen width, but never more than 1200.
const SCREEN_WIDTH = window.innerWidth;
const MAX_SCREEN_DIM = SCREEN_WIDTH * 0.75;
const MAX_BUBBLE_DIM = 1200;
const MAX_DIM = Math.min(MAX_SCREEN_DIM, MAX_BUBBLE_DIM);

/*
    Default image attachment. When pressed, it shows a modal with an image preview.
*/
export function MBChatImageAttachment({
  attachment,
  isSending,
  progress,
  forcedHeight,
  backgroundColor,
  containerWidth,
}: MBChatAttachmentProps) {
  const { src, width, height, ratio, isLandscape, isPortrait } = useImageSrcAndDimensions({
    image: attachment,
    containerWidth: MAX_DIM,
    containerHeight: MAX_DIM,
    size: 'contain',
  });
  const dpr = PixelRatio.get();
  let maxWidth = Math.min(
    MAX_DIM / dpr,
    !isNil(containerWidth) ? containerWidth * 0.75 : window.innerWidth * 0.75,
  );
  const maximumHeight = !isNil(forcedHeight)
    ? forcedHeight
    : Math.min(maxWidth / ratio, window.innerHeight * (MAX_VIEWPORT_HEIGHT_PCT / 100));
  if (!isNil(forcedHeight)) {
    maxWidth = forcedHeight * ratio;
  }

  return (
    <View
      center
      style={{
        width: isLandscape ? width : null,
        height: !isNil(forcedHeight) ? forcedHeight : isPortrait ? height : null,
        minWidth: MIN_IMAGE_SIZE,
        minHeight: MIN_IMAGE_SIZE,
        maxWidth: isLandscape ? maxWidth : null,
        maxHeight: isPortrait ? maximumHeight : null,
        backgroundColor: !isNil(backgroundColor) ? backgroundColor : null,
        flexShrink: 1,
      }}
    >
      {(isSending ?? isNumber(progress)) && (
        <View absolute center>
          <MBCircularProgressBar progress={progress} />
        </View>
      )}
      {!isNil(src) && (
        <img
          src={src}
          style={{
            maxWidth: isLandscape ? '100%' : null,
            maxHeight: isPortrait ? maximumHeight : null,
            width: isLandscape ? width : null,
            height: isPortrait ? height : null,
            opacity: isSending ? 0.5 : 1.0,
          }}
          alt=""
        />
      )}
    </View>
  );
}
